<template>
  <b-modal no-fade v-model="dialog.show" size="xl" :id="`modal-affiliate-${uniqueId}`"
           :hide-footer="true" centered @close="closeModal"
           no-close-on-backdrop
           dialog-class="am-dialog"
           body-class="am-body"
           header-class="am-modal-header"
  >
    <template #modal-title>
      <span v-if="isNewAffiliate">{{ $t('tabs.affiliate.newAffiliate') }}</span>
      <span v-else-if="loading !== true && error == null && affiliate != null">
        {{ displayName }}
      </span>
    </template>

    <div class="am-content" style="min-height: 620px">
      <!-- ERROR -->
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-if="loading !== true && error != null">
        <b-alert show variant="danger">
          <i class="fa-sharp fa-solid fa-triangle-exclamation"></i>
          {{ error }}
        </b-alert>
      </div>

      <!-- LOADING -->
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-else-if="loading === true">
        <b-spinner>{{ $t('operations.loading') }}</b-spinner>
      </div>

      <!-- CONTENT -->
      <template v-else>
        <!-- ============ NEW AFFILIATE (just the details form) ============ -->
        <div v-if="isNewAffiliate" class="am-new">
          <div class="am-section-head">
            <span class="am-section-icon"><i class="fa-solid fa-building-circle-exclamation"></i></span>
            <h4 class="am-section-title">{{ $t('tabs.affiliate.details') }}</h4>
          </div>
          <affiliate-edit-details ref="details" :new-affiliate="true" @submitted="onSubmitted" v-model="affiliate"/>
        </div>

        <!-- ============ EXISTING AFFILIATE (hero + tabs) ============ -->
        <div v-else>
          <!-- ---------- COMPACT HERO ---------- -->
          <div class="am-hero">
            <div class="am-hero-row">
              <div class="am-avatar" :class="{ online: affiliate.active, offline: !affiliate.active }">
                <span class="am-avatar-initials">{{ avatarInitials }}</span>
                <span class="am-presence" v-b-tooltip.hover
                      :title="affiliate.active ? $t('operations.active') : $t('operations.inactive')"></span>
              </div>
              <div class="am-hero-name-block">
                <div class="am-hero-name">
                  <span class="am-hero-name-text">{{ displayName }}</span>
                  <span class="am-pill am-pill-ok" v-if="affiliate.active">
                    <i class="fa-solid fa-circle-check"></i> {{ $t('operations.active') }}
                  </span>
                  <span class="am-pill am-pill-off" v-else>
                    <i class="fa-solid fa-circle-xmark"></i> {{ $t('operations.inactive') }}
                  </span>
                </div>
                <div class="am-hero-sub">
                  <span v-if="affiliate.alias" class="am-sub-item"><i class="fa-solid fa-at"></i> {{ affiliate.alias }}</span>
                  <span v-if="affiliate.email" class="am-sub-item"><i class="fa-regular fa-envelope"></i> {{ affiliate.email }}</span>
                  <span v-if="affiliate.skype" class="am-sub-item"><i class="fa-brands fa-skype"></i> {{ affiliate.skype }}</span>
                  <span v-if="affiliate.id" class="am-sub-item"><i class="fa-regular fa-id-badge"></i> #{{ affiliate.id }}</span>
                  <span v-if="affiliate.creationTime" class="am-sub-item">
                    <i class="fa-regular fa-calendar"></i> {{ $moment(affiliate.creationTime).format('DD/MM/YYYY') }}
                  </span>
                </div>
              </div>
              <div class="am-stats" v-if="affiliate.currentCpa">
                <div class="am-stat am-stat-cpa">
                  <div class="am-stat-icon"><i class="fa-solid fa-money-bill-wave"></i></div>
                  <div>
                    <div class="am-stat-label">{{ $t('columns.cpa') }}</div>
                    <div class="am-stat-value">{{ convertToEURO(affiliate.currentCpa.cpa) }}</div>
                  </div>
                </div>
                <div class="am-stat am-stat-crg">
                  <div class="am-stat-icon"><i class="fa-solid fa-percent"></i></div>
                  <div>
                    <div class="am-stat-label">{{ $t('columns.crg') }}</div>
                    <div class="am-stat-value">{{ affiliate.currentCpa.crg != null ? affiliate.currentCpa.crg : '—' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ACTION BAR -->
            <div class="am-action-bar">
              <button class="am-quick-action" :disabled="!affiliate.email" @click="quickEmail"
                      :title="$t('operations.sendMail')">
                <i class="fa-solid fa-envelope"></i><span>{{ $t('operations.sendMail') }}</span>
              </button>
              <button class="am-quick-action" :disabled="!affiliate.email" @click="copyEmail"
                      :title="$t('columns.email')">
                <i class="fa-solid fa-copy"></i><span>{{ $t('columns.email') }}</span>
              </button>
              <div class="am-action-spacer"></div>
              <b-button variant="primary" size="sm" class="am-save-btn" @click="saveDetails">
                <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
              </b-button>
            </div>
          </div>

          <!-- ---------- TABS ---------- -->
          <b-tabs v-model="dialog.tab" content-class="am-tabs-content" nav-class="am-tabs-nav" class="am-tabs">
            <b-tab active>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-circle-info"></i> {{ $t('tabs.affiliate.details') }}</span>
              </template>
              <affiliate-edit-details ref="details" :new-affiliate="false" @submitted="onSubmitted" v-model="affiliate"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-key"></i> {{ $t('tabs.affiliate.connectionManagement') }}</span>
              </template>
              <affiliate-token-table :affiliateId="affiliate.id"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-tags"></i> {{ $t('tabs.affiliate.priceManagement') }}</span>
              </template>
              <affiliate-edit-cpa v-model="affiliate"/>
            </b-tab>

            <b-tab lazy>
              <template #title>
                <span class="am-tab-pill"><i class="fa-solid fa-clock"></i> {{ $t('tabs.affiliate.timeManagement') }}</span>
              </template>
              <affiliate-edit-times :affiliate-id="affiliate.id" v-model="affiliate.openingTime"/>
            </b-tab>
          </b-tabs>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import { mapActions } from 'vuex';
import AffiliateEditDetails from '@/components/partial/affiliates/AffiliateEditDetails';
import Affiliate from '@/model/affiliate/Affiliate';
import AffiliateEditCpa from '@/components/partial/affiliates/AffiliateEditCpa';
import AffiliateEditTimes from '@/components/partial/affiliates/AffiliateEditTimes';
import AffiliateTokenTable from '@/components/tables/AffiliateTokenTable.vue'

export default {
  name: 'affiliateModal',
  components: { AffiliateTokenTable, AffiliateEditDetails , AffiliateEditCpa, AffiliateEditTimes},
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    dialog: {
      show: false,
      tab: 0,

    },
    uniqueId: 0,
    loading: true,
    error: null,
    id: null,
    affiliate: {},
  }),
  watch: {
    queryParams: {
      handler() {
        if (this.queryParams().affiliateId == null) {
          this.closeModal();
          return;
        }
        if (this.queryParams().affiliateId !== this.id) {
          this.showModal(this.queryParams().affiliateId);
        }
      },
      deep: true
    }
  },
  created() {
    this.uniqueId = this._uid;
    if (this.queryParams().affiliateId != null) {
      this.showModal(this.queryParams().affiliateId);
    }
  },
  computed: {
    isNewAffiliate(){
      return this.id == null || this.id === 0;
    },
    displayName(){
      if (this.affiliate == null) return '';
      return this.affiliate.company
          || this.affiliate.alias
          || this.affiliate.email
          || (this.affiliate.id != null ? `#${this.affiliate.id}` : '');
    },
    avatarInitials(){
      if (this.affiliate == null) return '?';
      const src = (this.affiliate.company || this.affiliate.alias || this.affiliate.email || '').trim();
      if (!src) return '?';
      const parts = src.split(/[\s@._-]+/).filter(Boolean);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts[1] ? parts[1][0] : '';
      return ((a + b) || a).toUpperCase() || '?';
    }
  },
  methods: {
    ...mapActions('affiliate', ['getAffiliateById']),
    showModal(id) {
      this.id = id;
      this.loading = true;
      this.dialog.show = true;
      this.dialog.tab = 0;
      if (!this.isNewAffiliate) {
        this.refreshData();
      } else {
        this.affiliate = Affiliate();
        this.error = null;
        this.loading = false;
      }
      this.pushState({
        affiliateId: id,
        tab: 0,
      });
    },
    closeModal() {
      this.dialog.show = false;
      this.affiliate = {};
      this.onDialogClose();
    },
    onDialogClose() {
      this.id = null;
      this.pushState({
        affiliateId: null,
        tab: null,
      });
    },
    refreshData() {
      let $this = this;
      this.getAffiliateById(this.id)
          .then(affiliate => {
            $this.affiliate = affiliate;
            $this.error = null;
            $this.loading = false;
          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
          });
    },
    onSubmitted(affiliate) {
      if (affiliate.id != null)
        this.id = affiliate.id;
      console.log('submitted', affiliate);
      this.refreshData()
      this.dialog.tab = 0;
    },
    // ---- Hero action-bar helpers (delegate to the mounted details component) ----
    quickEmail() {
      const d = this.$refs.details;
      if (d && typeof d.openEmailEditor === 'function') d.openEmailEditor();
    },
    saveDetails() {
      const d = this.$refs.details;
      if (d && typeof d.save === 'function') d.save();
    },
    async copyEmail() {
      if (this.affiliate == null || !this.affiliate.email) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.affiliate.email);
          this.showSuccessNotification(this.affiliate.email);
        }
      } catch (err) {
        console.error('Failed to copy email: ', err);
      }
    }
  }
};
</script>

<style scoped>
/* ===== Tokens (light) ===== */
.am-content {
  --am-bg: #ffffff;
  --am-bg-alt: #f8fafc;
  --am-border: #e2e8f0;
  --am-border-strong: #cbd5e1;
  --am-text: #0f172a;
  --am-text-2: #475569;
  --am-text-muted: #94a3b8;
  --am-primary: #2563eb;
  --am-primary-strong: #1d4ed8;
  --am-primary-soft: #eff6ff;
  --am-success: #16a34a;
  --am-success-soft: #f0fdf4;
  --am-warn: #f59e0b;
  --am-warn-soft: #fffbeb;
  --am-danger: #dc2626;
  --am-danger-soft: #fef2f2;

  padding: 0;
  color: var(--am-text);
}

.dark-layout .am-content {
  --am-bg: #1e293b;
  --am-bg-alt: #0f172a;
  --am-border: #334155;
  --am-border-strong: #475569;
  --am-text: #f1f5f9;
  --am-text-2: #cbd5e1;
  --am-text-muted: #94a3b8;
  --am-primary: #60a5fa;
  --am-primary-strong: #3b82f6;
  --am-primary-soft: rgba(96, 165, 250, 0.15);
  --am-success-soft: rgba(34, 197, 94, 0.15);
  --am-warn-soft: rgba(245, 158, 11, 0.15);
  --am-danger-soft: rgba(239, 68, 68, 0.15);
}

/* ===== NEW-AFFILIATE section head ===== */
.am-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.am-section-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--am-primary-soft);
  color: var(--am-primary);
  font-size: 0.95rem;
}
.am-section-title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: var(--am-text);
}

/* ===== HERO ===== */
.am-hero {
  position: relative;
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 50%, #fef3c7 100%);
  border-radius: 14px;
  padding: 11px 18px 11px;
  margin-bottom: 12px;
  border: 1px solid var(--am-border);
  overflow: hidden;
}

.dark-layout .am-hero {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.18) 0%, rgba(22, 163, 74, 0.12) 50%, rgba(245, 158, 11, 0.12) 100%);
  border-color: var(--am-border);
}

.am-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 0%, rgba(37, 99, 235, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.am-hero-row {
  display: flex;
  align-items: center;
  gap: 13px;
  flex-wrap: wrap;
  position: relative;
}

.am-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 50%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.02rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.am-presence {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid var(--am-bg);
  background: #cbd5e1;
}

.am-avatar.online .am-presence { background: var(--am-success); box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.25); }
.am-avatar.offline .am-presence { background: var(--am-danger); }

.am-hero-name-block { flex: 1; min-width: 0; }

.am-hero-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.am-hero-name-text {
  font-size: 1.08rem;
  font-weight: 800;
  color: var(--am-text);
  letter-spacing: -0.2px;
}

.am-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.am-pill-ok { background: var(--am-success-soft); color: var(--am-success); border: 1px solid rgba(22, 163, 74, 0.25); }
.am-pill-off { background: var(--am-danger-soft); color: var(--am-danger); border: 1px solid rgba(220, 38, 38, 0.25); }

.am-hero-sub {
  display: flex;
  gap: 14px;
  margin-top: 2px;
  font-size: 0.76rem;
  color: var(--am-text-2);
  flex-wrap: wrap;
}

.am-sub-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

/* Stats */
.am-stats { display: flex; gap: 10px; flex-wrap: wrap; }

.am-stat {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid var(--am-border);
  border-radius: 10px;
  padding: 5px 12px;
  min-width: 118px;
  display: flex;
  gap: 9px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.dark-layout .am-stat {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
}

.am-stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.am-stat-cpa .am-stat-icon { background: var(--am-success-soft); color: var(--am-success); }
.am-stat-crg .am-stat-icon { background: var(--am-primary-soft); color: var(--am-primary); }

.am-stat-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--am-text-muted);
}

.am-stat-value {
  font-size: 0.97rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--am-text);
  line-height: 1.15;
}

/* ===== ACTION BAR ===== */
.am-action-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  flex-wrap: wrap;
}

.dark-layout .am-action-bar { border-top-color: rgba(255, 255, 255, 0.08); }

.am-quick-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--am-border);
  color: var(--am-text);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.18s ease;
}

.dark-layout .am-quick-action {
  background: rgba(15, 23, 42, 0.6);
}

.am-quick-action:hover:not(:disabled) {
  background: var(--am-primary-soft);
  color: var(--am-primary);
  border-color: var(--am-primary);
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.18);
}

.am-quick-action:disabled { opacity: 0.45; cursor: not-allowed; }

.am-quick-action i { font-size: 0.85rem; }

.am-action-spacer { flex: 1; min-width: 6px; }

.am-save-btn {
  border-radius: 9px;
  padding: 6px 18px;
  font-weight: 700;
  font-size: 0.82rem;
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.3);
}
</style>

<style>
/* Global rules — b-modal renders outside the scoped component subtree */
.am-modal-header {
  border-bottom: 1px solid #e2e8f0;
}
.dark-layout .am-modal-header {
  border-bottom-color: #334155;
}

/* ===== TABS ===== */
.am-tabs .am-tabs-nav {
  border: none;
  border-bottom: 1px solid var(--am-border, #e2e8f0);
  padding: 0 4px;
  gap: 2px;
}

.am-tabs .am-tabs-nav .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 11px 18px 13px;
  font-size: 0.86rem;
  font-weight: 600;
  color: #94a3b8;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  background: transparent;
  margin-bottom: -1px;
}

.am-tabs .am-tabs-nav .nav-link:hover {
  color: #2563eb;
  background: #eff6ff;
}

.am-tabs .am-tabs-nav .nav-link.active {
  color: #2563eb;
  background: transparent;
  border-bottom: 2px solid #2563eb;
  font-weight: 700;
}

.dark-layout .am-tabs .am-tabs-nav { border-bottom-color: #334155; }
.dark-layout .am-tabs .am-tabs-nav .nav-link:hover {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
}
.dark-layout .am-tabs .am-tabs-nav .nav-link.active {
  color: #60a5fa;
  border-bottom-color: #60a5fa;
}

.am-tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.am-tab-pill i { font-size: 0.88rem; }

.am-tabs .am-tabs-content {
  padding: 18px 6px 6px;
}
</style>
